import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import { apiRequest } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { ApiError } from '../../utils/apiClient.js';

/**
 * Table component for displaying Twilio phone numbers with Parrot assignment support
 */
const TwilioNumbersTable = ({
  numbers = [],
  loading = false,
  selectedNumber = null,
  onNumberSelect = () => {},
  onAssignNumber = () => {},
  onUnassignNumber = () => {},
  onReleaseNumber = () => {},
  onBulkAssign = () => {},
  onDragStart = () => {},
  onDragEnd = () => {}
}) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  
  const [sortBy, setSortBy] = useState('phone_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filter, setFilter] = useState('all'); // all, assigned, unassigned
  const [confirmUnassign, setConfirmUnassign] = useState(null); // number to unassign
  
  // Bulk assignment state
  const [selectedNumbers, setSelectedNumbers] = useState(new Set());
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);
  const [bulkAssignParrot, setBulkAssignParrot] = useState('');
  const [availableParrots, setAvailableParrots] = useState([]);
  const [loadingParrots, setLoadingParrots] = useState(false);
  
  // Parrot assignment state
  const [showParrotAssignment, setShowParrotAssignment] = useState(false);
  const [numberForParrotAssignment, setNumberForParrotAssignment] = useState(null);
  const [selectedParrotForAssignment, setSelectedParrotForAssignment] = useState('');

  /**
   * Fetch available parrots for assignment
   */
  const fetchAvailableParrots = useCallback(async () => {
    try {
      setLoadingParrots(true);
      const token = await getToken();
      const response = await apiRequest('/api/admin/parrots?limit=1000', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setAvailableParrots(data.parrots || []);
    } catch (error) {
      console.error('Error fetching parrots:', error);
      showToast('Failed to load parrots', 'error');
    } finally {
      setLoadingParrots(false);
    }
  }, [getToken, showToast]);

  /**
   * Handle bulk number selection
   */
  const handleNumberSelection = (numberId, selected) => {
    const newSelection = new Set(selectedNumbers);
    if (selected) {
      newSelection.add(numberId);
    } else {
      newSelection.delete(numberId);
    }
    setSelectedNumbers(newSelection);
  };

  /**
   * Handle select all/none
   */
  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      const unassignedNumbers = sortedNumbers
        .filter(n => !n.assigned_user_id)
        .map(n => n.id);
      setSelectedNumbers(new Set(unassignedNumbers));
    } else {
      setSelectedNumbers(new Set());
    }
  };

  /**
   * Handle bulk assignment
   */
  const handleBulkAssignSubmit = async () => {
    if (!bulkAssignParrot || selectedNumbers.size === 0) {
      showToast('Please select a parrot and at least one phone number', 'error');
      return;
    }

    try {
      const selectedParrot = availableParrots.find(p => p.id === bulkAssignParrot);
      if (!selectedParrot) {
        showToast('Selected parrot not found', 'error');
        return;
      }

      // Call the bulk assignment handler
      await onBulkAssign(Array.from(selectedNumbers), bulkAssignParrot, selectedParrot.name);
      
      // Reset state
      setSelectedNumbers(new Set());
      setShowBulkAssignment(false);
      setBulkAssignParrot('');
      
    } catch (error) {
      console.error('Bulk assignment error:', error);
      showToast('Failed to assign phone numbers', 'error');
    }
  };

  /**
   * Handle individual parrot assignment
   */
  const handleParrotAssignSubmit = async () => {
    if (!selectedParrotForAssignment || !numberForParrotAssignment) {
      showToast('Please select a parrot', 'error');
      return;
    }

    try {
      console.log('Parrot assignment attempt:', {
        selectedParrotForAssignment,
        selectedParrotType: typeof selectedParrotForAssignment,
        availableParrots: availableParrots.map(p => ({ id: p.id, idType: typeof p.id, name: p.name }))
      });

      const selectedParrot = availableParrots.find(p => String(p.id) === String(selectedParrotForAssignment));
      if (!selectedParrot) {
        console.error('Parrot not found in available parrots:', {
          selectedParrotForAssignment,
          availableParrotIds: availableParrots.map(p => p.id)
        });
        showToast('Selected parrot not found', 'error');
        return;
      }

      // Call the assignment handler with parrot information
      await onAssignNumber(numberForParrotAssignment, selectedParrotForAssignment, selectedParrot.name);
      
      // Reset state
      setShowParrotAssignment(false);
      setNumberForParrotAssignment(null);
      setSelectedParrotForAssignment('');
      
    } catch (error) {
      console.error('Parrot assignment error:', error);
      showToast('Failed to assign phone number to parrot', 'error');
    }
  };

  /**
   * Handle parrot unassignment (keep user assignment)
   */
  const handleParrotUnassignSubmit = async () => {
    if (!numberForParrotAssignment) {
      showToast('No phone number selected', 'error');
      return;
    }

    try {
      console.log('Parrot unassignment attempt:', {
        phoneNumber: numberForParrotAssignment.phone_number,
        currentParrot: numberForParrotAssignment.assigned_parrot_name,
        keepUser: numberForParrotAssignment.assigned_user_name
      });

      // Call the assignment handler with null parrot to unassign from parrot but keep user
      await onAssignNumber(numberForParrotAssignment, null, null);
      
      // Reset state
      setShowParrotAssignment(false);
      setNumberForParrotAssignment(null);
      setSelectedParrotForAssignment('');
      
    } catch (error) {
      console.error('Parrot unassignment error:', error);
      showToast('Failed to unassign phone number from parrot', 'error');
    }
  };

  /**
   * Open parrot assignment modal
   */
  const openParrotAssignment = (number) => {
    setNumberForParrotAssignment(number);
    setSelectedParrotForAssignment(number.assigned_parrot_id || '');
    setShowParrotAssignment(true);
    if (availableParrots.length === 0) {
      fetchAvailableParrots();
    }
  };

  /**
   * Handle column sorting
   */
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  /**
   * Sort numbers based on current sort settings
   */
  const sortedNumbers = React.useMemo(() => {
    let filtered = numbers;
    
    // Apply filter
    if (filter === 'assigned') {
      filtered = numbers.filter(n => n.assigned_user_id);
    } else if (filter === 'unassigned') {
      filtered = numbers.filter(n => !n.assigned_user_id);
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [numbers, sortBy, sortOrder, filter]);

  /**
   * Get sort icon for column header
   */
  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  /**
   * Get color scheme for parrot visual indicators
   */
  const getParrotColor = (parrotId) => {
    const colors = [
      { 
        bg: theme === 'dark' ? 'bg-blue-900/10' : 'bg-blue-50', 
        border: 'border-l-4 border-blue-500',
        dot: 'bg-blue-500'
      },
      { 
        bg: theme === 'dark' ? 'bg-green-900/10' : 'bg-green-50', 
        border: 'border-l-4 border-green-500',
        dot: 'bg-green-500'
      },
      { 
        bg: theme === 'dark' ? 'bg-purple-900/10' : 'bg-purple-50', 
        border: 'border-l-4 border-purple-500',
        dot: 'bg-purple-500'
      },
      { 
        bg: theme === 'dark' ? 'bg-orange-900/10' : 'bg-orange-50', 
        border: 'border-l-4 border-orange-500',
        dot: 'bg-orange-500'
      },
      { 
        bg: theme === 'dark' ? 'bg-pink-900/10' : 'bg-pink-50', 
        border: 'border-l-4 border-pink-500',
        dot: 'bg-pink-500'
      },
      { 
        bg: theme === 'dark' ? 'bg-indigo-900/10' : 'bg-indigo-50', 
        border: 'border-l-4 border-indigo-500',
        dot: 'bg-indigo-500'
      }
    ];
    
    // Use parrot ID to consistently assign colors
    const colorIndex = parseInt(parrotId) % colors.length;
    return colors[colorIndex];
  };

  /**
   * Handle number release with confirmation
   */
  const handleReleaseClick = (number, event) => {
    event.stopPropagation();
    
    if (window.confirm(
      `Are you sure you want to release ${number.phone_number}? This action cannot be undone.`
    )) {
      onReleaseNumber(number.id);
    }
  };

  /**
   * Handle unassign click - show confirmation
   */
  const handleUnassignClick = (number, event) => {
    event.stopPropagation();
    setConfirmUnassign(number);
  };

  /**
   * Confirm unassignment
   */
  const handleConfirmUnassign = () => {
    if (confirmUnassign) {
      onUnassignNumber(confirmUnassign);
      setConfirmUnassign(null);
    }
  };

  /**
   * Cancel unassignment
   */
  const handleCancelUnassign = () => {
    setConfirmUnassign(null);
  };

  // Load parrots when component mounts
  useEffect(() => {
    fetchAvailableParrots();
  }, [fetchAvailableParrots]);

  if (loading) {
    return (
      <div className={"rounded-lg p-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"}>
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className={`ml-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading phone numbers...</span>
        </div>
      </div>
    );
  }

  if (numbers.length === 0) {
    return (
      <div className={"rounded-lg p-8 text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"}>
        <div className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>📞</div>
        <h3 className={"text-lg font-medium mb-2 text-gray-900 dark:text-white"}>No Phone Numbers</h3>
        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
          No phone numbers found in your Twilio account.
        </p>
      </div>
    );
  }

  return (
    <div className={"rounded-lg overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"}>
      {/* Filter tabs */}
      <div className={`border-b px-6 py-4 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex space-x-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-teal-600 text-white'
                : theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            All ({numbers.length})
          </button>
          <button
            onClick={() => setFilter('assigned')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              filter === 'assigned'
                ? 'bg-teal-600 text-white'
                : theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Assigned ({numbers.filter(n => n.assigned_user_id).length})
          </button>
          <button
            onClick={() => setFilter('unassigned')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              filter === 'unassigned'
                ? 'bg-teal-600 text-white'
                : theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Available ({numbers.filter(n => !n.assigned_user_id).length})
          </button>
        </div>
      </div>

      {/* Bulk assignment controls */}
      {selectedNumbers.size > 0 && (
        <div className={`border-b px-6 py-4 ${theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-blue-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {selectedNumbers.size} number{selectedNumbers.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedNumbers(new Set())}
                className={`text-sm underline transition-colors ${
                  theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Clear selection
              </button>
            </div>
            <button
              onClick={() => {
                setShowBulkAssignment(true);
                if (availableParrots.length === 0) {
                  fetchAvailableParrots();
                }
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm dark:text-white"
            >
              Assign to Parrot
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedNumbers.size > 0 && selectedNumbers.size === sortedNumbers.filter(n => !n.assigned_user_id).length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
                  />
                  <span>Select</span>
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                onClick={() => handleSort('phone_number')}
              >
                <div className="flex items-center space-x-1">
                  <span>Phone Number</span>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}>{getSortIcon('phone_number')}</span>
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}>{getSortIcon('status')}</span>
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                onClick={() => handleSort('assigned_user_name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Assigned User</span>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}>{getSortIcon('assigned_user_name')}</span>
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:bg-gray-600'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
                onClick={() => handleSort('assigned_parrot_name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Assigned Parrot</span>
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}>{getSortIcon('assigned_parrot_name')}</span>
                </div>
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Capabilities
              </th>
              <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {sortedNumbers.map((number) => {
              const isSelected = selectedNumbers.has(number.id);
              const parrotColor = number.assigned_parrot_id ? getParrotColor(number.assigned_parrot_id) : null;
              
              return (
                <tr
                  key={number.id}
                  draggable={number.assigned_user_id ? true : false}
                  onDragStart={(e) => onDragStart(e, number)}
                  onDragEnd={onDragEnd}
                  className={`cursor-pointer transition-colors ${
                    selectedNumber?.id === number.id
                      ? theme === 'dark'
                        ? 'bg-teal-900/20 border-l-4 border-teal-500'
                        : 'bg-teal-50 border-l-4 border-teal-500'
                      : parrotColor && number.assigned_parrot_id
                        ? `${parrotColor.bg} ${parrotColor.border}`
                        : theme === 'dark'
                          ? 'hover:bg-gray-700'
                          : 'hover:bg-gray-50'
                  } ${number.assigned_user_id ? 'cursor-move' : ''}`}
                  onClick={() => onNumberSelect(number)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleNumberSelection(number.id, e.target.checked);
                      }}
                      disabled={number.assigned_user_id}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {number.assigned_user_id && (
                        <div 
                          className={`text-gray-400 cursor-move ${theme === 'dark' ? 'hover:text-gray-300' : 'hover:text-gray-600'}`}
                          title="Drag to assign to Parrot"
                        >
                          ⋮⋮
                        </div>
                      )}
                      <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {number.phone_number}
                      </div>
                      {number.assigned_parrot_id && parrotColor && (
                        <div 
                          className={`w-3 h-3 rounded-full ${parrotColor.dot}`}
                          title={`Assigned to ${number.assigned_parrot_name}`}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    number.assigned_user_id
                      ? theme === 'dark' ? 'bg-teal-900/20 text-teal-400' : 'bg-teal-100 text-teal-800'
                      : theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                      {number.assigned_user_id ? 'assigned' : 'unassigned'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {number.assigned_user_name || (
                        <span className={`italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Unassigned</span>
                      )}
                    </div>
                    {number.assigned_user_email && (
                      <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {number.assigned_user_email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {number.assigned_parrot_name || (
                        <span className={`italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {(number.capabilities || ['Voice', 'SMS']).map((capability) => (
                        <span
                          key={capability}
                          className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            theme === 'dark' ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {capability}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {number.assigned_user_id ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openParrotAssignment(number);
                            }}
                            className="text-purple-400 hover:text-purple-300 transition-colors"
                            title="Assign to Parrot"
                          >
                            🦜
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onAssignNumber(number);
                            }}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                            title="Reassign number"
                          >
                            🔄
                          </button>
                          <button
                            onClick={(e) => handleUnassignClick(number, e)}
                            className="text-yellow-400 hover:text-yellow-300 transition-colors"
                            title="Unassign number"
                          >
                            ➖
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignNumber(number);
                          }}
                          className="text-green-400 hover:text-green-300 transition-colors"
                          title="Assign number"
                        >
                          ➕
                        </button>
                      )}
                      <button
                        onClick={(e) => handleReleaseClick(number, e)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Release number"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedNumbers.length === 0 && (
        <div className="p-8 text-center">
          <div className={`mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No numbers match the current filter</div>
          <button
            onClick={() => setFilter('all')}
            className={`underline transition-colors ${
              theme === 'dark' ? 'text-teal-400 hover:text-teal-300' : 'text-teal-600 hover:text-teal-700'
            }`}
          >
            Show all numbers
          </button>
        </div>
      )}

      {/* Unassign Confirmation Dialog */}
      {confirmUnassign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-100'
                  }`}>
                    <span className={`text-xl ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>⚠️</span>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Unassign Phone Number
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    This action will unassign the number but keep it in your Twilio account.
                  </p>
                </div>
              </div>
              
              <div className={`rounded-lg p-4 mb-6 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`font-medium mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {confirmUnassign.phone_number}
                  </div>
                  <div>
                    Currently assigned to: <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{confirmUnassign.assigned_user_name}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelUnassign}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUnassign}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors dark:text-white"
                >
                  Unassign Number
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assignment Modal */}
      {showBulkAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Bulk Assign to Parrot
                </h3>
                <button
                  onClick={() => setShowBulkAssignment(false)}
                  className={`text-gray-400 hover:text-gray-600 ${theme === 'dark' ? 'hover:text-gray-300' : ''}`}
                >
                  ✕
                </button>
              </div>
              
              <div className={`rounded-lg p-4 mb-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Assigning {selectedNumbers.size} phone number{selectedNumbers.size !== 1 ? 's' : ''} to a Parrot
                </div>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Parrot
                </label>
                {loadingParrots ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                    <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Loading parrots...
                    </span>
                  </div>
                ) : (
                  <select
                    value={bulkAssignParrot}
                    onChange={(e) => setBulkAssignParrot(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select a parrot...</option>
                    {availableParrots.map((parrot) => (
                      <option key={parrot.id} value={parrot.id}>
                        {parrot.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkAssignment(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkAssignSubmit}
                  disabled={!bulkAssignParrot || loadingParrots}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 dark:text-white"
                >
                  Assign Numbers
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parrot Assignment Modal */}
      {showParrotAssignment && numberForParrotAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Assign to Parrot
                </h3>
                <button
                  onClick={() => setShowParrotAssignment(false)}
                  className={`text-gray-400 hover:text-gray-600 ${theme === 'dark' ? 'hover:text-gray-300' : ''}`}
                >
                  ✕
                </button>
              </div>
              
              <div className={`rounded-lg p-4 mb-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`font-medium mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {numberForParrotAssignment.phone_number}
                  </div>
                  <div>
                    Currently assigned to: <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {numberForParrotAssignment.assigned_user_name}
                    </span>
                    {numberForParrotAssignment.assigned_parrot_name && (
                      <span> → {numberForParrotAssignment.assigned_parrot_name}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Parrot
                </label>
                {loadingParrots ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                    <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Loading parrots...
                    </span>
                  </div>
                ) : (
                  <select
                    value={selectedParrotForAssignment}
                    onChange={(e) => setSelectedParrotForAssignment(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select a parrot...</option>
                    {availableParrots.map((parrot) => (
                      <option key={parrot.id} value={parrot.id}>
                        {parrot.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowParrotAssignment(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                {numberForParrotAssignment?.assigned_parrot_name && (
                  <button
                    onClick={handleParrotUnassignSubmit}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors dark:text-white"
                  >
                    Unassign from Parrot
                  </button>
                )}
                <button
                  onClick={handleParrotAssignSubmit}
                  disabled={!selectedParrotForAssignment || loadingParrots}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 dark:text-white"
                >
                  Assign to Parrot
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwilioNumbersTable;
